import { useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import { useRillContext } from "../../rill/RillReact/share/base";
import { canSwap } from "../../utils/exercise-validations";
import { isCoach } from "../../utils/user-helpers";
import { getPermissionNamesByIds } from "../../views/dashboard/settings/manage-coaches/utils/manage-coaches-utils";
import { SearchDropdown } from "../inputs/search-dropdown";
import Modal from "../modal-new";
import { getExerciseName } from "./learn-more/blockHelpers";

const SwapExerciseModal = ({
  exercise,
  isProgramBuilder,
  programNumber,
  cycleId,
  cycleOwner,
}) => {
  const { t } = useTranslation("coaching-portal");
  const { locals } = useRillContext();
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const buildMovementOptions = () => {
    const { activeUserToRequest } = locals;
    const { exerciseLibrary } = activeUserToRequest;

    const libraryArr = [];

    Object.entries(exerciseLibrary).forEach(([primary, movements]) => {
      if (!["assistant", "other"].includes(primary)) {
        libraryArr.push({
          movementLabel: getExerciseName({
            type: "primary",
            secondary: null,
            primary,
          }),
          value: primary,
          primary,
          type: "primary",
          secondary: null,
          movement: primary[0].toUpperCase() + primary.slice(1),
        });
      }

      movements.forEach((item) => {
        const type = primary === "assistant" ? "assistant" : "secondary";
        const movement = {
          ...item,
          movementLabel: getExerciseName({
            secondary: item.movement,
            primary,
            type,
          }),
          value: `${primary}${item.movement}`,
          primary,
          type,
          secondary: item.movement,
        };
        libraryArr.push(movement);
      });
    });

    const movementOptions = libraryArr.filter((libraryExercise) =>
      canSwap(exercise, libraryExercise)
    );

    return movementOptions;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const { query } = locals;
    if (submitted) return;
    setSubmitted(true);

    if (!confirm(t("swapExercise.confirmSwap"))) {
      return setSubmitted(false);
    }

    if (selectedExercise) {
      const body = {
        typeFrom: exercise.type,
        primaryFrom: exercise.primary,
        secondaryFrom: exercise.secondary,
        typeTo: selectedExercise.type,
        primaryTo: selectedExercise.primary,
        movementTo: selectedExercise.movement,
        startDate: exercise.date,
        proxyAthlete: cycleOwner || query.athlete,
        ...(programNumber && { programNumber, cycleId }),
      };

      submit("/actions/exercise/swap/", {
        method: "POST",
        body,
      });
    }
  };

  const {
    activeUser,
    organizationPermissions: globalOrganizationPermissions = [],
  } = locals;
  const { organizationPermissions = [], organizationId } = activeUser;

  const movementOptions = buildMovementOptions();
  const libraryLink = isCoach(activeUser)
    ? "/coaching-portal/exercise-library"
    : "/dashboard/settings/exercise-library";

  const userOrganizationPermission =
    organizationPermissions.length > 0
      ? getPermissionNamesByIds(
          globalOrganizationPermissions,
          organizationPermissions
        )
      : [""];

  const userOrganizationCanAccessToExerciseLibrary =
    !organizationId ||
    (!!organizationId &&
      userOrganizationPermission.includes("Exercise Library"));

  return (
    <form onSubmit={handleSubmit} id="swap-exercise">
      <Modal
        toClear={["editMode", "exercise"]}
        className="Swap-Exercise"
        title={t("swapExercise.title")}
      >
        <div className="body">
          <p className="p4">{t("swapExercise.existingExercise")}</p>

          <p className="s1 mt-1">{getExerciseName(exercise)}</p>

          <label className="mt-6">
            {t("swapExercise.selectExerciseToSwapWith")}
          </label>

          <SearchDropdown
            options={movementOptions}
            placeholder={t("swapExercise.selectExercisePlaceholder")}
            valueKey="movementLabel"
            handleChange={setSelectedExercise}
            value={selectedExercise?.movementLabel}
          />

          {selectedExercise ? (
            <div className="chip warning mt-8 items-start">
              <div className="flex flex-col gap-4">
                <span>
                  This will swap all instances of{" "}
                  <span className="font-bold">{getExerciseName(exercise)}</span>{" "}
                  for{" "}
                  <span className="font-bold">
                    {getExerciseName(selectedExercise)}
                  </span>{" "}
                  {isProgramBuilder
                    ? "within this block."
                    : "from this workout forward."}
                </span>
                <span>{t("swapExercise.cannotBeUndone")}</span>
              </div>
            </div>
          ) : (
            <div className="chip info mt-8 items-start">
              <div className="flex flex-col gap-4">
                <span>{t("swapExercise.onlySwapSameParams")}</span>
                <span>{t("swapExercise.createNewInLibrary")}</span>
              </div>
            </div>
          )}
        </div>

        <footer className="justify-between!">
          {userOrganizationCanAccessToExerciseLibrary ? (
            <a role="button" data-button-type="secondary" href={libraryLink}>
              {t("swapExercise.exerciseLibrary")}
            </a>
          ) : (
            <div className="invisible"></div>
          )}

          <div className="flex gap-2">
            <a
              role="button"
              data-button-type="text-white"
              data-action="cancel"
              href="/actions/refresh?modal[type]=&exercise="
            >
              {t("common:cancel")}
            </a>
            <button data-button-type="primary" type="submit">
              {t("swapExercise.swap")}
            </button>
          </div>
        </footer>
      </Modal>
    </form>
  );
};

export default SwapExerciseModal;
